import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  Settings,
  Inbox,
  Activity,
  Building2,
  Files,
  ChevronRight,
  CircleDot,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  DollarSign,
  Share2,
  BookOpen,
  ClipboardCheck,
  Hammer,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { DiscloserMark } from '@/components/onboarding-v2/OnboardingFlowV2';

// ─────────────────────────────────────────────────────────────────────────────
// HeroProductPreview
//
// A wide stylized representation of the Discloser app, anchored at the bottom
// of the hero and designed to bleed into the next section (Linear-style).
// Same visual language as the actual app — paper-warm surfaces, brand-ink
// text, severity tokens, the Discloser mark — so the eventual demo-video
// swap won't feel jarring.
//
// Two variants:
//   agents  — 3-pane app shell: workspace rail / findings canvas / Closer
//             drafted-reply panel with [Edit | Send] buttons.
//   brokers — 3-pane dashboard: workspace rail / listings table /
//             alert-detail panel with [Notify | Review] buttons.
//
// Mobile (<md) collapses to just the Closer/alert panel — the rest of the
// app shell is decorative and harms phone reading at that width.
//
// Layout stability:
//   - Outer container has a fixed aspect-ratio reservation so the box is
//     pinned before paint.
//   - Inner scroll regions use overflow-hidden — no internal scroll affects
//     surrounding flow.
//   - Buttons are aria-hidden / tabIndex=-1 — purely illustrative; the real
//     primary CTA is the page button above.
// ─────────────────────────────────────────────────────────────────────────────

export type HeroProductPreviewVariant = 'agents' | 'brokers';

interface Props {
  variant: HeroProductPreviewVariant;
}

export default function HeroProductPreview({ variant }: Props) {
  return (
    <div className="mx-auto w-full max-w-[1100px] [contain:layout_style]" aria-hidden="false">
      {/* Desktop / tablet — full 3-pane shell */}
      <div className="hidden md:block">
        <FullShell variant={variant} />
      </div>
      {/* Mobile — collapsed single-pane Closer/alert card */}
      <div className="block md:hidden">
        <MobilePanel variant={variant} />
      </div>
    </div>
  );
}

// ─── Desktop full shell ──────────────────────────────────────────────────────

function FullShell({ variant }: Props) {
  return (
    <div className="relative rounded-2xl border border-black/10 bg-white shadow-[0_40px_80px_-20px_rgba(26,22,21,0.35)] overflow-hidden">
      <Toolbar variant={variant} />
      <div className="flex h-[440px] lg:h-[520px]">
        <LeftRail variant={variant} />
        <MainCanvas variant={variant} />
        <RightPanel variant={variant} />
      </div>
    </div>
  );
}

function Toolbar({ variant }: Props) {
  return (
    <div className="flex items-center gap-3 border-b border-black/5 px-3.5 py-2.5 bg-brand-paper-warm/40">
      <div className="flex items-center gap-1.5 mr-1">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" aria-hidden />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" aria-hidden />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" aria-hidden />
      </div>
      <div className="flex items-center gap-1.5">
        <DiscloserMark size={14} />
        <span className="font-display text-[13px] font-semibold text-brand-ink">
          {variant === 'brokers' ? 'Bay Pacific Realty' : 'Sarah K. · Coldwell Banker'}
        </span>
      </div>
      <div className="flex-1 flex justify-center">
        <div className="flex items-center gap-1.5 max-w-[280px] w-full rounded-md border border-black/5 bg-white px-2.5 h-7 font-meta text-[11px] text-brand-text-3">
          <Search className="w-3 h-3" strokeWidth={2.4} />
          <span>Search disclosures, agents, listings…</span>
        </div>
      </div>
      <Settings className="w-3.5 h-3.5 text-brand-text-3" strokeWidth={2.2} />
    </div>
  );
}

// ─── Left rail ───────────────────────────────────────────────────────────────

function LeftRail({ variant }: Props) {
  return (
    <aside className="w-[200px] lg:w-[220px] shrink-0 border-r border-black/5 bg-brand-paper-warm/40 px-3 py-3.5 flex flex-col gap-2.5 [contain:layout_style]">
      <div className="font-meta text-[9.5px] uppercase tracking-[0.14em] text-brand-text-3 px-1.5">
        Workspace
      </div>
      <nav className="flex flex-col gap-0.5">
        <RailItem icon={Files} label="Listings" active />
        <RailItem icon={Inbox} label="Inbox" badge={variant === 'brokers' ? '7' : '3'} />
        <RailItem icon={Activity} label="Activity" />
        {variant === 'brokers' && <RailItem icon={Building2} label="Compliance" />}
      </nav>

      {variant === 'agents' ? (
        <>
          {/* Mirrors SummarySectionNav — listing-specific Deal + Disclosures */}
          <div className="font-meta text-[9.5px] uppercase tracking-[0.14em] text-brand-text-3 px-1.5 mt-1.5">
            Deal
          </div>
          <nav className="flex flex-col gap-0.5">
            <RailItem icon={MessageSquare} label="Messages" badge="2" />
            <RailItem icon={DollarSign} label="Offers" />
            <RailItem icon={Share2} label="Share" />
          </nav>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.14em] text-brand-text-3 px-1.5 mt-1.5">
            Disclosures
          </div>
          <nav className="flex flex-col gap-0.5">
            <RailItem icon={BookOpen} label="Overview" />
            <RailItem icon={ClipboardCheck} label="Findings" active />
            <RailItem icon={Hammer} label="Permits" />
            <RailItem icon={FileText} label="Documents" />
          </nav>
        </>
      ) : (
        <>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.14em] text-brand-text-3 px-1.5 mt-1.5">
            Active listings
          </div>
          <div className="flex flex-col gap-0.5">
            <ListingItem label="142 Maple Ave" sub="Sarah K." active />
            <ListingItem label="88 Elm Street" sub="Marcus T." />
            <ListingItem label="240 Pine Road" sub="Sarah K." />
            <ListingItem label="71 Oak Lane" sub="Jen L." />
          </div>
        </>
      )}

      <div className="mt-auto flex items-center gap-2 px-1.5 pt-2 border-t border-black/5">
        <div className="w-6 h-6 rounded-full bg-brand-cream/60 border border-black/10 flex items-center justify-center font-display text-[10px] font-semibold text-brand-ink">
          {variant === 'brokers' ? 'CP' : 'SK'}
        </div>
        <div className="leading-tight">
          <div className="font-brand text-[11.5px] font-medium text-brand-ink">
            {variant === 'brokers' ? 'Christine P.' : 'Sarah K.'}
          </div>
          <div className="font-meta text-[9.5px] text-brand-text-3">
            {variant === 'brokers' ? 'Managing broker' : 'Listing agent'}
          </div>
        </div>
      </div>
    </aside>
  );
}

function RailItem({
  icon: Icon,
  label,
  active,
  badge,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  badge?: string;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-md px-1.5 py-1.5 ${
        active ? 'bg-white shadow-[0_1px_2px_rgba(26,22,21,0.05)] text-brand-ink' : 'text-brand-text-2'
      }`}
    >
      <Icon className="w-3.5 h-3.5 text-brand-text-3" strokeWidth={2.2} />
      <span className="font-brand text-[12px] font-medium">{label}</span>
      {badge && (
        <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full bg-brand-ink text-white font-meta text-[9px] font-semibold">
          {badge}
        </span>
      )}
    </div>
  );
}

function ListingItem({
  label,
  sub,
  active,
}: {
  label: string;
  sub: string;
  active?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-md px-1.5 py-1.5 ${
        active ? 'bg-white shadow-[0_1px_2px_rgba(26,22,21,0.05)]' : ''
      }`}
    >
      <div className="leading-tight min-w-0">
        <div className="font-brand text-[11.5px] font-medium text-brand-ink truncate">
          {label}
        </div>
        <div className="font-meta text-[9.5px] text-brand-text-3 truncate">{sub}</div>
      </div>
      {active && <ChevronRight className="w-3 h-3 text-brand-text-3 shrink-0" strokeWidth={2.2} />}
    </div>
  );
}

// ─── Main canvas ─────────────────────────────────────────────────────────────

function MainCanvas({ variant }: Props) {
  return (
    <main className="flex-1 min-w-0 [contain:layout_style] overflow-hidden">
      {variant === 'agents' ? <AgentsCanvas /> : <BrokersCanvas />}
    </main>
  );
}

function AgentsCanvas() {
  return (
    <div className="h-full flex flex-col">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 px-5 py-3 border-b border-black/5 bg-white">
        <span className="font-meta text-[10px] uppercase tracking-[0.14em] text-brand-text-3">
          Listings
        </span>
        <ChevronRight className="w-3 h-3 text-brand-text-3" strokeWidth={2.2} />
        <span className="font-display text-[13px] font-semibold text-brand-ink">
          142 Maple Ave
        </span>
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-4 px-5 border-b border-black/5">
        <Tab label="Summary" />
        <Tab label="Findings" active />
        <Tab label="Messages" />
        <Tab label="Audit" />
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-black/5 bg-brand-paper-warm/30">
        <Stat label="Findings" value="12" />
        <Divider />
        <Stat label="High" value="1" tone="critical" />
        <Divider />
        <Stat label="Warning" value="1" tone="warning" />
        <Divider />
        <Stat label="Cleared" value="1" tone="info" />
        <Divider />
        <Stat label="Pages cited" value="47" />
      </div>

      {/* Finding cards */}
      <div className="flex-1 px-5 py-3 space-y-2.5 overflow-hidden">
        <FindingCard
          severity="high"
          section="Section II.A.10"
          title="Missing seismic strap"
          body={
            <>
              AVID inspection notes "no visible strap on water heater." Seller marked{' '}
              <em>"unknown"</em> on TDS. Verify or remediate before close.
            </>
          }
          page="AVID p. 47 · TDS p. 9"
          highlighted
        />
        <FindingCard
          severity="warning"
          section="Section II.A.10"
          title="Roof age 21 yrs · approaching end-of-life"
          body="Inspection noted granule loss on south slope. Buyer may request credit; not a deal-breaker."
          page="Inspect. p. 12"
        />
        <FindingCard
          severity="cleared"
          section="Section II.A.6"
          title="Water-heater compliance language"
          body="Standard CA disclosure boilerplate. Every CA listing has this. Not a flag."
          page="TDS p. 4"
        />
        <FindingCard
          severity="low"
          section="Section II.B.3"
          title="Sewer lateral inspection on file"
          body="Recent video inspection passed. Permit on record."
          page="NHD p. 22"
        />
      </div>
    </div>
  );
}

function BrokersCanvas() {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-1.5 px-5 py-3 border-b border-black/5 bg-white">
        <span className="font-meta text-[10px] uppercase tracking-[0.14em] text-brand-text-3">
          Brokerage
        </span>
        <ChevronRight className="w-3 h-3 text-brand-text-3" strokeWidth={2.2} />
        <span className="font-display text-[13px] font-semibold text-brand-ink">Overview</span>
      </div>

      <div className="flex items-center gap-2 px-5 py-3 border-b border-black/5 bg-brand-paper-warm/30">
        <Stat label="Active listings" value="12" />
        <Divider />
        <Stat label="Disclosure-clean" value="9" tone="info" />
        <Divider />
        <Stat label="Attention" value="3" tone="critical" />
        <Divider />
        <Stat label="Avg time-to-clean" value="2.3d" />
      </div>

      <div className="flex-1 px-5 py-4 overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="font-meta text-[9.5px] uppercase tracking-[0.14em] text-brand-text-3">
              <th className="pb-2 font-meta">Listing</th>
              <th className="pb-2 font-meta">Agent</th>
              <th className="pb-2 font-meta">Status</th>
              <th className="pb-2 font-meta text-right">Days</th>
            </tr>
          </thead>
          <tbody className="font-brand text-[12px] text-brand-ink">
            <BrokerRow listing="142 Maple Ave" agent="Sarah K." status="stalled" days="2" highlighted />
            <BrokerRow listing="88 Elm Street" agent="Marcus T." status="re-sign" days="3" />
            <BrokerRow listing="240 Pine Road" agent="Sarah K." status="clean" days="—" />
            <BrokerRow listing="71 Oak Lane" agent="Jen L." status="clean" days="—" />
            <BrokerRow listing="503 Bay Vista" agent="Marcus T." status="clean" days="—" />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tab({ label, active }: { label: string; active?: boolean }) {
  return (
    <div
      className={`relative h-9 flex items-center font-brand text-[12.5px] font-medium ${
        active ? 'text-brand-ink' : 'text-brand-text-3'
      }`}
    >
      {label}
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand-ink rounded-full" />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'critical' | 'warning' | 'info' | 'muted';
}) {
  const valueColor =
    tone === 'critical'
      ? 'text-brand-critical'
      : tone === 'warning'
      ? 'text-brand-high'
      : tone === 'info'
      ? 'text-brand-info'
      : tone === 'muted'
      ? 'text-brand-text-3'
      : 'text-brand-ink';
  return (
    <div className="leading-tight">
      <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3">
        {label}
      </div>
      <div className={`font-display text-[15px] font-semibold tabular-nums ${valueColor}`}>
        {value}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="w-px h-7 bg-black/5 mx-1" aria-hidden />;
}

type FindingSeverity = 'cleared' | 'low' | 'warning' | 'high';

function FindingCard({
  severity,
  section,
  title,
  body,
  page,
  highlighted,
}: {
  severity: FindingSeverity;
  section: string;
  title: string;
  body: React.ReactNode;
  page: string;
  highlighted?: boolean;
}) {
  const severityClasses: Record<FindingSeverity, string> = {
    high: 'bg-brand-critical-bg/50 border-brand-critical/25',
    warning: 'bg-brand-high-bg/50 border-brand-high/25',
    cleared: 'bg-brand-info-bg/40 border-brand-info/25',
    low: 'bg-brand-paper-warm/40 border-black/5',
  };
  const dotClasses: Record<FindingSeverity, string> = {
    high: 'text-brand-critical',
    warning: 'text-brand-high',
    cleared: 'text-brand-info',
    low: 'text-brand-text-3',
  };
  const Dot = severity === 'cleared' ? CheckCircle2 : CircleDot;
  return (
    <div
      className={`rounded-lg border ${severityClasses[severity]} px-3.5 py-2.5 ${
        highlighted ? 'shadow-[0_2px_6px_-3px_rgba(199,48,55,0.25)]' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <Dot className={`w-3 h-3 ${dotClasses[severity]}`} strokeWidth={2.4} />
          <span className="font-meta text-[10px] uppercase tracking-[0.12em] text-brand-text-3">
            {section}
          </span>
        </div>
        <span className="font-meta text-[10px] text-brand-text-3">{page}</span>
      </div>
      <div className="font-brand text-[12.5px] font-semibold text-brand-ink mb-0.5">{title}</div>
      <div className="font-brand text-[11.5px] text-brand-text-2 leading-snug">{body}</div>
    </div>
  );
}

function BrokerRow({
  listing,
  agent,
  status,
  days,
  highlighted,
}: {
  listing: string;
  agent: string;
  status: 'stalled' | 're-sign' | 'clean';
  days: string;
  highlighted?: boolean;
}) {
  const statusConfig = {
    stalled: { label: 'Stalled', icon: AlertTriangle, color: 'text-brand-critical' },
    're-sign': { label: 'Re-sign', icon: AlertTriangle, color: 'text-brand-high' },
    clean: { label: 'Clean', icon: CheckCircle2, color: 'text-brand-info' },
  } as const;
  const cfg = statusConfig[status];
  const Icon = cfg.icon;
  return (
    <tr className={`border-t border-black/5 ${highlighted ? 'bg-brand-critical-bg/30' : ''}`}>
      <td className="py-2 font-medium">{listing}</td>
      <td className="py-2 text-brand-text-2">{agent}</td>
      <td className="py-2">
        <span className={`inline-flex items-center gap-1 ${cfg.color} font-meta text-[11px]`}>
          <Icon className="w-3 h-3" strokeWidth={2.4} /> {cfg.label}
        </span>
      </td>
      <td className="py-2 text-right tabular-nums text-brand-text-2">{days}</td>
    </tr>
  );
}

// ─── Right panel (Closer's drafted reply / alert detail) ─────────────────────

function RightPanel({ variant }: Props) {
  return (
    <aside className="w-[320px] lg:w-[360px] shrink-0 border-l border-black/5 bg-gradient-to-b from-brand-sky/10 via-brand-cream/10 to-white px-4 py-4 flex flex-col [contain:layout_style]">
      {variant === 'agents' ? <AgentsRightPanel /> : <BrokersRightPanel />}
    </aside>
  );
}

// Chat-flow animation: Mark asks → Closer types → Closer replies → drafted-email
// indicator appears → buttons stay pinned at the bottom. Same step pattern as
// DealEngineDemo, scoped to this panel. Slots have reserved heights so the
// surrounding shell never reflows mid-loop.
const CHAT_TOTAL_STEPS = 8;
const CHAT_STEP_INTERVAL_MS = 1400;
const CHAT_FINAL_STEP = CHAT_TOTAL_STEPS - 1;

function chatPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function AgentsRightPanel() {
  const reducedMotion = useMemo(chatPrefersReducedMotion, []);
  const [step, setStep] = useState(reducedMotion ? CHAT_FINAL_STEP : 0);
  const [running, setRunning] = useState(true);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (reducedMotion) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => setRunning(entries[0]?.isIntersecting ?? true),
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (!running || reducedMotion) return;
    const id = setInterval(() => {
      setStep((prev) => (prev + 1) % CHAT_TOTAL_STEPS);
    }, CHAT_STEP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [running, reducedMotion]);

  // Buttons highlight at step 5 (review pause) — pulses subtly to show "agent
  // can act now". Stays highlighted after.
  const buttonsHighlight = step >= 5;

  return (
    <div ref={ref} className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-sky/30 via-brand-cream/20 to-white border border-brand-sky/30 flex items-center justify-center">
          <DiscloserMark size={13} />
        </div>
        <div className="leading-tight">
          <div className="font-brand text-[12.5px] font-semibold text-brand-ink">
            Closer · Live thread
          </div>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3">
            142 Maple Ave · with Mark T.
          </div>
        </div>
      </div>

      {/* Chat thread — reserved bubble slots, opacity-only reveal */}
      <div className="space-y-2.5">
        {/* Mark's incoming question */}
        <ChatBubbleSlot reservedH="min-h-[78px]" visible={step >= 1}>
          <BuyerBubble />
        </ChatBubbleSlot>

        {/* Closer's response — typing dots at step 2; full reply at step ≥ 3 */}
        <ChatBubbleSlot reservedH="min-h-[126px]" visible={step >= 2}>
          <CloserBubble showTyping={step === 2} />
        </ChatBubbleSlot>

        {/* Drafted email indicator pill */}
        <ChatBubbleSlot reservedH="min-h-[34px]" visible={step >= 4}>
          <div className="rounded-md border border-black/5 bg-white px-2.5 py-1.5 font-meta text-[10px] text-brand-text-3">
            <span className="text-brand-info">●</span> Drafted email to John (listing agent) ·
            ready to send
          </div>
        </ChatBubbleSlot>
      </div>

      {/* Buttons row — pinned to bottom; subtle highlight when chat reaches review state */}
      <div className="mt-auto flex items-center justify-end gap-2">
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center h-7 px-3 rounded-pill bg-white border border-brand-ink/15 text-brand-ink font-meta text-[10.5px] font-medium tracking-wide transition-colors hover:bg-brand-ink/5"
        >
          Edit
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          style={{
            transform: buttonsHighlight ? 'translateY(-1px) scale(1.02)' : 'none',
            boxShadow: buttonsHighlight
              ? '0 6px 16px -6px rgba(26,22,21,0.45)'
              : '0 0 0 0 rgba(0,0,0,0)',
            transition: 'transform 400ms ease-out, box-shadow 400ms ease-out',
          }}
          className="inline-flex items-center gap-1 h-7 px-3 rounded-pill bg-brand-ink text-white font-meta text-[10.5px] font-semibold tracking-wide"
        >
          Send to Mark <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}

function ChatBubbleSlot({
  reservedH,
  visible,
  children,
}: {
  reservedH: string;
  visible: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={reservedH}>
      <div
        className={`transition-opacity duration-500 ease-out ${
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function BuyerBubble() {
  return (
    <div className="rounded-xl bg-white border border-black/5 px-3 py-2 shadow-[0_1px_2px_rgba(26,22,21,0.04)]">
      <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3 mb-1">
        Mark T. · Buyer's agent
      </div>
      <div className="font-brand text-[12.5px] text-brand-ink leading-snug">
        What does <strong>Section II.A.6</strong> mean? Should I be worried?
      </div>
    </div>
  );
}

function CloserBubble({ showTyping }: { showTyping: boolean }) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-brand-sky/15 via-brand-cream/10 to-white border border-brand-sky/30 px-3 py-2">
      <div className="flex items-center gap-1.5 font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3 mb-1">
        <DiscloserMark size={11} />
        Closer · just now
      </div>
      <div className="font-brand text-[12.5px] text-brand-ink leading-snug min-h-[68px]">
        {showTyping ? (
          <TypingDots />
        ) : (
          <>
            <strong>Not a flag.</strong> Section II.A.6 is standard CA water-heater compliance.{' '}
            <em>Real flag</em> is II.A.10 — AVID notes a missing seismic strap.
          </>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1.5" aria-hidden>
      <span
        className="w-1.5 h-1.5 rounded-full bg-brand-text-3 animate-pulse"
        style={{ animationDelay: '0ms', animationDuration: '1.2s' }}
      />
      <span
        className="w-1.5 h-1.5 rounded-full bg-brand-text-3 animate-pulse"
        style={{ animationDelay: '180ms', animationDuration: '1.2s' }}
      />
      <span
        className="w-1.5 h-1.5 rounded-full bg-brand-text-3 animate-pulse"
        style={{ animationDelay: '360ms', animationDuration: '1.2s' }}
      />
    </div>
  );
}

function BrokersRightPanel() {
  return (
    <>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-info-bg via-brand-cream/20 to-white border border-brand-info/30 flex items-center justify-center">
          <DiscloserMark size={13} />
        </div>
        <div className="leading-tight">
          <div className="font-brand text-[12.5px] font-semibold text-brand-ink">
            Stalled-listing alert
          </div>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3">
            142 Maple Ave · Sarah K.
          </div>
        </div>
      </div>

      <div className="font-brand text-[12.5px] text-brand-ink leading-snug mb-3">
        Buyer-agent question stalled <strong>2 days</strong>. Closer drafted Sarah's reply
        plus the email to the seller about Section II.A.10. Awaiting Sarah's review.
      </div>

      <div className="rounded-md border border-black/5 bg-white px-2.5 py-1.5 mb-3 font-meta text-[10px] text-brand-text-3">
        <span className="text-brand-high">●</span> Re-sign on II.A.10 needed before close ·
        7 days out
      </div>

      <div className="mt-auto flex items-center justify-end gap-2">
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center h-7 px-3 rounded-pill bg-white border border-brand-ink/15 text-brand-ink font-meta text-[10.5px] font-medium tracking-wide transition-colors hover:bg-brand-ink/5"
        >
          Notify Sarah
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center gap-1 h-7 px-3 rounded-pill bg-brand-ink text-white font-meta text-[10.5px] font-semibold tracking-wide transition-transform hover:-translate-y-[1px] hover:scale-[1.02] active:scale-[0.97]"
        >
          Review draft <span aria-hidden>→</span>
        </button>
      </div>
    </>
  );
}

// ─── Mobile single-pane ──────────────────────────────────────────────────────

function MobilePanel({ variant }: Props) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white shadow-[0_30px_60px_-30px_rgba(26,22,21,0.4)] overflow-hidden">
      <div className="px-4 py-4 bg-gradient-to-b from-brand-sky/10 via-brand-cream/10 to-white">
        {variant === 'agents' ? <MobileAgentsPanel /> : <MobileBrokersPanel />}
      </div>
    </div>
  );
}

function MobileAgentsPanel() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-sky/30 via-brand-cream/20 to-white border border-brand-sky/30 flex items-center justify-center">
          <DiscloserMark size={13} />
        </div>
        <div className="leading-tight min-w-0">
          <div className="font-brand text-[12.5px] font-semibold text-brand-ink">
            Closer · Live thread
          </div>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3 truncate">
            142 Maple Ave · with Mark T.
          </div>
        </div>
      </div>

      <div className="space-y-2.5">
        <BuyerBubble />
        <CloserBubble showTyping={false} />
        <div className="rounded-md border border-black/5 bg-white px-2.5 py-1.5 font-meta text-[10px] text-brand-text-3">
          <span className="text-brand-info">●</span> Drafted email to John · ready to send
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center h-7 px-3 rounded-pill bg-white border border-brand-ink/15 text-brand-ink font-meta text-[10.5px] font-medium tracking-wide"
        >
          Edit
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center gap-1 h-7 px-3 rounded-pill bg-brand-ink text-white font-meta text-[10.5px] font-semibold tracking-wide"
        >
          Send to Mark <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}

function MobileBrokersPanel() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-info-bg via-brand-cream/20 to-white border border-brand-info/30 flex items-center justify-center">
          <DiscloserMark size={13} />
        </div>
        <div className="leading-tight min-w-0">
          <div className="font-brand text-[12.5px] font-semibold text-brand-ink">
            Stalled-listing alert
          </div>
          <div className="font-meta text-[9.5px] uppercase tracking-[0.12em] text-brand-text-3 truncate">
            142 Maple Ave · Sarah K.
          </div>
        </div>
      </div>

      <div className="font-brand text-[12.5px] text-brand-ink leading-snug mb-3">
        Buyer-agent question stalled <strong>2 days</strong>. Closer drafted Sarah's
        reply and the email to the seller.
      </div>

      <div className="rounded-md border border-black/5 bg-white px-2.5 py-1.5 mb-4 font-meta text-[10px] text-brand-text-3">
        <span className="text-brand-high">●</span> Re-sign on II.A.10 needed before close
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center h-7 px-3 rounded-pill bg-white border border-brand-ink/15 text-brand-ink font-meta text-[10.5px] font-medium tracking-wide"
        >
          Notify Sarah
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="inline-flex items-center gap-1 h-7 px-3 rounded-pill bg-brand-ink text-white font-meta text-[10.5px] font-semibold tracking-wide"
        >
          Review draft <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}
